import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Models
import { MetaData, PaginatedData, ResponseData } from 'src/app/platform/models/utils';
import { PaymentsData, BankData, CardData, DebitCardData, DebitCBUData, PaymentGenerateData } from 'src/app/modules/payments/models/payments';

// Services
import { UserService } from 'src/app/modules/account/user.service';
import { ApiService } from 'src/app/platform/services/api.service';
import { dateFormat, objectToString } from 'src/app/platform/utils/functions';

/**
 * This class handles all actions related to payments
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentsService {

  constructor(
    private apiService: ApiService,
    private userService: UserService,
  ) { }

  /**
   * Get a list of payments
   */
  paymentsGet(page?: number | null, limit?: number | null, filters: any | null = null): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      let data: any = {page, limit};

      if (filters !== null) {
        data = {
          page,
          limit,
          estados: filters.status,
          medioPago: filters.paymentMethod,
          fechaEmisionDesde: dateFormat(filters.date.start, 'YYYY/MM/DD 00:00:00'),
          fechaEmisionHasta: dateFormat(filters.date.end, 'YYYY/MM/DD 23:59:59'),
          fechaPagoDesde: dateFormat(filters.datePayment.start, 'YYYY/MM/DD 00:00:00'),
          fechaPagoHasta: dateFormat(filters.datePayment.end, 'YYYY/MM/DD 23:59:59'),
        };
      }

      this.apiService.request('get', 'pagos/' + this.userService.currentUser.value.client + objectToString(data), {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const paymentsData: Array<PaymentsData> = [];

          success.items.forEach((value: any, index: number) => {
            paymentsData.push(new PaymentsData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', new PaginatedData(paymentsData, success.totalRegistros, success.limiteRegistros, success.paginas, success.paginaActual)));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Report a payment made
   */
  paymentReport(date: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('post', 'facturas/' + this.userService.currentUser.value.client + '/informarpago/' + date, {}, headers).toPromise().then((success: any) => {
        if(success.response.status){
          resolve(this.apiService.successDataPrepare('', success));
        } else {
          reject(this.apiService.errorDataPrepare(success.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Generate a payment process according to invoices sent
   */
  paymentGenerate(method: 'mercadopago'|'pagos360', receipts: Array<PaymentGenerateData>, cancelUnsuscribe: boolean = false): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      const data = {
        gateway: method === 'pagos360' ? 'Pagos360' : 'MercadoPago',
        restablecerServicio: cancelUnsuscribe,
        comprobantes: receipts,
      };

      this.apiService.request('post', 'pagos/' + this.userService.currentUser.value.client + '/', data, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          resolve(this.apiService.successDataPrepare('', success));
        } else {
          reject(this.apiService.errorDataPrepare(success.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of payment methods
   */
   callsTypesGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'pagos/' + this.userService.currentUser.value.client + '/mediospago', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const responseData: Array<MetaData> = [];

          success.mediosPago.forEach((value: any, index: number) => {
            responseData.push(new MetaData(value, value));
          });

          resolve(this.apiService.successDataPrepare('', responseData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Get a list of banks
   */
  banksGet(): Promise<ResponseData>{
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'bancos/bancos', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const banksData: Array<BankData> = [];

          success.items.forEach((value: any, index: number) => {
            banksData.push(new BankData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', banksData));
        } else{
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Get a list of credit cards
   */
  cardsGet(): Promise<ResponseData>{
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'tarjetas/tarjetas', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const cardsData: Array<CardData> = [];

          success.items.forEach((value: any, index: number) => {
            cardsData.push(new CardData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', cardsData));
        } else{
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Get a list of debit cards
   */
  debitGet(type: string): Promise<ResponseData>{
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'clientes/' + this.userService.currentUser.value.client + '/' + ((type === 'credit') ? 'obtenerdebitotarjeta' : 'obtenerdebitocbu'), {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          let debitData: any;

          if (type === 'credit') {
            debitData = new DebitCardData().configData(success);
          } else {
            debitData = new DebitCBUData().configData(success);
          }

          resolve(this.apiService.successDataPrepare('', debitData));
        } else{
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Set up the debit of an account
   */
  debitAdd(type: string, holder: string, dni: string, bank: BankData, cbu: string, cardNumber: string, cardEntity: CardData, cardDateExpiration: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };
      let process = false;
      let data: any;

      if (type === 'credit') {
        process = true;

        data = {
          banco: { id: bank.id, nombre: bank.name },
          tarjeta: { id: cardEntity.id, nombre: cardEntity.name },
          tarjetaDni: dni,
          tarjetaNro: cardNumber,
          tarjetaTitular: holder,
          tarjetaVto: cardDateExpiration,
        };
      } else {
        process = true;

        data = {
          banco: { id: bank.id, nombre: bank.name },
          debitoDni: dni,
          debitoNro: cbu,
          debitoTitular: holder,
        };
      }

      if (process) {
        this.apiService.request('post', 'clientes/' + this.userService.currentUser.value.client + '/' + ((type === 'credit') ? 'adherirdebitotarjeta' : 'adherirdebitocbu'), data, headers).toPromise().then((success: any) => {
          if(success.status){
            resolve(this.apiService.successDataPrepare('Débito configurado correctamente', success));
          } else {
            reject(this.apiService.errorDataPrepare(success.message, success));
          }
        }, (error) => {
          reject(this.apiService.errorDataPrepare(error.message, error));
        });
      } else {
        reject(this.apiService.errorDataPrepare('El parámetro type debe ser del tipo cbu o credit.', type));
      }
    });
  }

  /**
   * Remove the debit of an account
   */
  debitRemove(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('post', 'clientes/' + this.userService.currentUser.value.client + '/anulardebito', {}, headers).toPromise().then((success: any) => {
        if (success.status) {
          resolve(this.apiService.successDataPrepare('Débito configurado correctamente', success));
        } else {
          reject(this.apiService.errorDataPrepare(success.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.message, error));
      });
    });
  }

  /**
   * Generate a PDF file from a given HTML
   */
  paymentReceiptPDF(receip: HTMLElement, name: string): void {
    html2canvas(receip).then(canvas => {
      const pdf = new jsPDF('p', 'mm', 'a4');

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, 90, 90);
      pdf.save('comprobante-pago-' + name + '.pdf');
    });
  }
}
